/**
 * OrganizerTeam.js
 * ─────────────────────────────────────────────────────────────────────────────
 * The organizer's team screen: who works here, what role they hold, what that
 * role actually permits, and per-person exceptions on top.
 *
 * Visual language is lifted from views/RBACManager.js (the platform RBAC UI) so
 * the two read as one system: the same `S.*` style table, the same role chips,
 * the same modal shell, the same permission matrix.
 *
 * Two house rules this file obeys deliberately:
 *   1. `<Table responsive>` is DEAD on this dashboard — the theme sets
 *      `.table-responsive { overflow: visible }` at >=992px, so a wide table
 *      silently overflows its card instead of scrolling. Every wide table here
 *      is wrapped in its own `overflow-x: auto` container.
 *   2. No browser `confirm()`. Destructive actions route through ConfirmModal,
 *      which can explain the consequence (suspending somebody revokes their
 *      gate access and their terminal PIN) instead of asking "Are you sure?".
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  S,
  getAvatarColor,
  getInitials,
  fmtDate,
  apiErrMsg,
  LoadingCenter,
  Alert,
  Btn,
  Field,
  ConfirmModal,
  RoleChip,
  StatusPill,
  OverridesModal,
  RoleEditorDialog,
  TeamRoleSelect,
} from "../components/team/index.js";
import { AppButton, AppDialog, AppSelect } from "../components/ui/index.js";

import { apiTicketingMyOrganizers } from "../api/index.js";
import EmailDeliveryBadge from "../components/EmailDeliveryBadge.js";
import {
  apiOrganizerRbacCatalogue,
  apiOrganizerMyAccess,
  apiOrganizerListMembers,
  apiOrganizerAddMember,
  apiOrganizerChangeMemberRole,
  apiOrganizerSetMemberOverrides,
  apiOrganizerSetMemberStatus,
  apiOrganizerRemoveMember,
  apiOrganizerCreateRole,
  apiOrganizerUpdateRole,
  apiOrganizerDeleteRole,
} from "../api/organizerRbac.js";

// Styles, primitives, RoleChip, StatusPill, OverridesModal, the role editor and
// the role dropdown live in components/team/ and are shared with the vendor
// team page; dialogs, dropdowns and buttons are the components/ui kit.

// ---------------------------------------------------------------------------
// MODALS
// ---------------------------------------------------------------------------

const AddMemberModal = ({ roles, onClose, onSubmit, busy, error }) => {
  const [form, setForm] = useState({ name: "", email: "", phoneNumber: "", roleKey: "GATE" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const role = roles.find((r) => r.key === form.roleKey);
  const canSubmit = !busy && Boolean(form.email || form.phoneNumber);

  return (
    <AppDialog
      open
      title="Add a team member"
      subtitle="Link an existing Lusuku account, or create one and email them a set-password link."
      busy={busy}
      onClose={onClose}
      footer={
        <>
          <AppButton variant="quiet" onClick={onClose} disabled={busy}>Cancel</AppButton>
          <AppButton icon="fa fa-user-plus" loading={busy} disabled={!canSubmit} onClick={() => onSubmit(form)}>
            Add member
          </AppButton>
        </>
      }
    >
      <Alert type="error" msg={error} />
      <Field label="Name" htmlFor="org-member-name">
        <input id="org-member-name" className="lk-input" value={form.name} onChange={set("name")} placeholder="Full name" data-autofocus />
      </Field>
      <div className="lk-field-grid">
        <Field label="Email" htmlFor="org-member-email">
          <input id="org-member-email" className="lk-input" type="email" value={form.email} onChange={set("email")} placeholder="name@example.com" />
        </Field>
        <Field label="Phone" htmlFor="org-member-phone">
          <input id="org-member-phone" className="lk-input" value={form.phoneNumber} onChange={set("phoneNumber")} placeholder="+256..." />
        </Field>
      </div>
      <Field label="Role" htmlFor="org-member-role" hint={role?.description}>
        <TeamRoleSelect
          id="org-member-role"
          roles={roles}
          value={form.roleKey}
          onChange={(roleKey) => setForm((f) => ({ ...f, roleKey }))}
          block
        />
      </Field>
      <div className="lk-note lk-note--info">
        <i className="fa fa-envelope" aria-hidden="true" style={{ marginTop: 2 }} />
        <span>
          No password to copy or share: a new login gets an email link to set its own password. Phone-only
          members get a one-time password, shown once on this screen.
        </span>
      </div>
    </AppDialog>
  );
};

// ---------------------------------------------------------------------------
// MAIN VIEW
// ---------------------------------------------------------------------------

const OrganizerTeam = () => {
  const [organizers, setOrganizers] = useState([]);
  const [organizerId, setOrganizerId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [inviteDeliveryRef, setInviteDeliveryRef] = useState("");
  const [modalError, setModalError] = useState("");
  const [tab, setTab] = useState("members");

  const [catalogue, setCatalogue] = useState({ permissions: [], roles: [], legacyImplicitRole: "MANAGER" });
  const [myAccess, setMyAccess] = useState(null);
  const [members, setMembers] = useState([]);
  const [legacyCount, setLegacyCount] = useState(0);

  const [addOpen, setAddOpen] = useState(false);
  const [roleModal, setRoleModal] = useState(null); // { role } | { role: null }
  const [overridesFor, setOverridesFor] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  const canManage = Boolean(myAccess?.permissions?.includes("team:manage"));
  const grantableKeys = useMemo(
    () => new Set(myAccess?.isOwner || myAccess?.isSuperUser
      ? catalogue.permissions.map((p) => p.key)
      : myAccess?.permissions || []),
    [myAccess, catalogue.permissions]
  );

  const roleByKey = useMemo(() => {
    const m = new Map();
    for (const r of catalogue.roles) m.set(r.key, r);
    return m;
  }, [catalogue.roles]);

  // ── data ------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiTicketingMyOrganizers();
        const list = res.data?.organizers || [];
        if (cancelled) return;
        setOrganizers(list);
        setOrganizerId((current) => current || (list[0] ? String(list[0]._id) : ""));
      } catch (e) {
        if (!cancelled) setError(apiErrMsg(e, "Failed to load your organizers."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadTeam = useCallback(
    async (id, { silent } = {}) => {
      if (!id) return;
      if (!silent) setLoading(true);
      setError("");
      try {
        const [cat, mine, list] = await Promise.all([
          apiOrganizerRbacCatalogue(id),
          apiOrganizerMyAccess(id),
          apiOrganizerListMembers(id),
        ]);
        setCatalogue(cat.data?.data || { permissions: [], roles: [] });
        setMyAccess(mine.data?.data || null);
        setMembers(list.data?.data?.members || []);
        setLegacyCount(list.data?.data?.legacyImplicitCount || 0);
      } catch (e) {
        setError(apiErrMsg(e, "Failed to load the team."));
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (organizerId) loadTeam(organizerId);
  }, [organizerId, loadTeam]);

  // ── actions ---------------------------------------------------------------
  const run = async (fn, successMsg) => {
    setBusy(true);
    setModalError("");
    try {
      const result = await fn();
      await loadTeam(organizerId, { silent: true });
      if (successMsg) setNotice(typeof successMsg === "function" ? successMsg(result) : successMsg);
      return true;
    } catch (e) {
      setModalError(apiErrMsg(e, "That did not work."));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async (form) => {
    const ok = await run(async () => {
      const res = await apiOrganizerAddMember(organizerId, form);
      const data = res.data?.data;
      // Live delivery verification for the invite that was just sent —
      // the badge next to the notice polls this ref and turns green
      // (delivered) or red (bounced) instead of leaving "emailed" as the
      // last word. See components/EmailDeliveryBadge.js.
      setInviteDeliveryRef(data?.credentialsEmailed ? data.deliveryRefId || "" : "");
      return data;
    }, (data) =>
      data?.credentialsEmailed
        ? `Added ${form.name || form.email}. They've been emailed a link to set their own password (sent to ${data.sentTo}).`
        : data?.tempPassword
        ? `Added ${form.name || form.email}. ${data.mailFallback ? "The invite email could not be sent, so use this one" : "One"}-time password: ${data.tempPassword} — copy it now, it is not shown again.`
        : `Added ${form.name || form.email}.`
    );
    if (ok) setAddOpen(false);
  };

  const handleRoleChange = (member, roleKey) =>
    run(
      () => apiOrganizerChangeMemberRole(organizerId, member.userId, roleKey),
      `Role updated to ${roleByKey.get(roleKey)?.label || roleKey}.`
    );

  const handleSaveRole = async (payload) => {
    const existing = roleModal?.role;
    const ok = await run(
      () =>
        existing
          ? apiOrganizerUpdateRole(organizerId, existing.key, payload)
          : apiOrganizerCreateRole(organizerId, payload),
      existing ? "Role saved." : "Role created."
    );
    if (ok) setRoleModal(null);
  };

  const handleSaveOverrides = async (overrides) => {
    const ok = await run(
      () => apiOrganizerSetMemberOverrides(organizerId, overridesFor.userId, overrides),
      "Exceptions saved."
    );
    if (ok) setOverridesFor(null);
  };

  const confirmSuspend = (member) =>
    setConfirmState({
      title: `Suspend ${member.name || member.email}?`,
      consequence:
        "They keep their login but lose every permission on this organizer immediately. They are removed from the terminal cashier roster, their POS PIN stops working, and they can no longer scan at the gate. You can reactivate them at any time.",
      confirmLabel: "Suspend",
      variant: "warning",
      action: () => apiOrganizerSetMemberStatus(organizerId, member.userId, "SUSPENDED"),
      successMsg: "Member suspended.",
    });

  const confirmRemove = (member) =>
    setConfirmState({
      title: `Remove ${member.name || member.email} from the team?`,
      consequence:
        "Their access ends immediately and they leave the cashier roster. Their Lusuku login is NOT deleted (it may be a personal account) and everything they did stays in the activity log. Adding them back later restores their history.",
      confirmLabel: "Remove",
      variant: "danger",
      action: () => apiOrganizerRemoveMember(organizerId, member.userId),
      successMsg: "Member removed.",
    });

  const confirmDeleteRole = (role) =>
    setConfirmState({
      title: `Delete the ${role.label} role?`,
      consequence:
        "The role is removed from this organizer. Members still holding it must be moved to another role first, otherwise the deletion is refused.",
      confirmLabel: "Delete role",
      variant: "danger",
      action: () => apiOrganizerDeleteRole(organizerId, role.key),
      successMsg: "Role deleted.",
      onDone: () => setRoleModal(null),
    });

  const runConfirm = async () => {
    const state = confirmState;
    const ok = await run(state.action, state.successMsg);
    if (ok) {
      setConfirmState(null);
      state.onDone?.();
    } else {
      setError(modalError);
      setConfirmState(null);
    }
  };

  // ── render ----------------------------------------------------------------
  if (loading && !members.length) {
    return (
      <div style={S.page}>
        <LoadingCenter text="Loading your team..." />
      </div>
    );
  }

  const activeCount = members.filter((m) => m.status === "ACTIVE").length;
  const suspendedCount = members.filter((m) => m.status === "SUSPENDED").length;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h2 style={S.title}>Team and permissions</h2>
          <div style={S.subtitle}>
            Who works for this organizer, what they may do, and the exceptions on top.
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {organizers.length > 1 && (
            <AppSelect aria-label="Organizer" value={organizerId} onChange={(e) => setOrganizerId(e.target.value)}>
              {organizers.map((o) => (
                <option key={o._id} value={o._id}>
                  {o.name}
                </option>
              ))}
            </AppSelect>
          )}
          {canManage && tab === "members" && (
            <AppButton icon="fa fa-user-plus" onClick={() => { setModalError(""); setAddOpen(true); }}>
              Add member
            </AppButton>
          )}
          {canManage && tab === "roles" && (
            <AppButton icon="fa fa-plus" onClick={() => { setModalError(""); setRoleModal({ role: null }); }}>
              New role
            </AppButton>
          )}
        </div>
      </div>

      <Alert type="error" msg={error} onClose={() => setError("")} />
      <Alert type="success" msg={notice} onClose={() => setNotice("")} />
      {notice && inviteDeliveryRef ? (
        <div style={{ margin: "-6px 0 14px" }}>
          <EmailDeliveryBadge refId={inviteDeliveryRef} />
        </div>
      ) : null}

      {myAccess && (
        <div style={S.kpiGrid}>
          <div style={S.kpi("#083860")}>
            <div style={S.kpiVal}>{activeCount}</div>
            <div style={S.kpiLabel}>Active members</div>
          </div>
          <div style={S.kpi("#b45309")}>
            <div style={S.kpiVal}>{suspendedCount}</div>
            <div style={S.kpiLabel}>Suspended</div>
          </div>
          <div style={S.kpi("#047857")}>
            <div style={S.kpiVal}>{catalogue.roles.length}</div>
            <div style={S.kpiLabel}>Roles available</div>
          </div>
          <div style={S.kpi("#7c3aed")}>
            <div style={S.kpiVal}>{myAccess.permissions?.length || 0}</div>
            <div style={S.kpiLabel}>Your permissions ({myAccess.roleKey})</div>
          </div>
        </div>
      )}

      {legacyCount > 0 && (
        <Alert
          type="warning"
          msg={`${legacyCount} member(s) predate roles and are being treated as ${catalogue.legacyImplicitRole}. Give each of them an explicit role, or run scripts/backfillOrganizerMembers.js, so their access is deliberate rather than inherited.`}
        />
      )}

      <div style={S.tabBar}>
        <button style={S.tab(tab === "members")} onClick={() => setTab("members")}>Members</button>
        <button style={S.tab(tab === "roles")} onClick={() => setTab("roles")}>Roles and permissions</button>
      </div>

      {tab === "members" && (
        <div style={S.card}>
          <div style={S.sectionTitle}>Members ({members.length})</div>
          {/* Own scroll container -- <Table responsive> does not scroll on this theme. */}
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Member</th>
                  <th style={S.th}>Role</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Permissions</th>
                  <th style={S.th}>Exceptions</th>
                  <th style={S.th}>Last active</th>
                  <th style={S.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => {
                  const role = roleByKey.get(m.roleKey);
                  const editable = canManage && !m.isOwner && m.userId !== myAccess?.userId;
                  return (
                    <tr key={m.userId}>
                      <td style={S.td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={S.avatar(getAvatarColor(m.name || m.email || "?"))}>
                            {getInitials(m.name || m.email || "?")}
                          </span>
                          <span>
                            <div style={{ fontWeight: 600 }}>{m.name || "Unnamed"}</div>
                            <div style={{ fontSize: 11, color: "#64748b" }}>{m.email || m.phoneNumber || m.userId}</div>
                            {m.implicit && (
                              <span style={{ ...S.pill("#b45309", "#fef3c7"), marginTop: 4, display: "inline-block" }}>
                                No explicit role yet
                              </span>
                            )}
                          </span>
                        </div>
                      </td>
                      <td style={S.td}>
                        {editable ? (
                          <TeamRoleSelect
                            roles={catalogue.roles}
                            value={m.roleKey}
                            onChange={(roleKey) => handleRoleChange(m, roleKey)}
                            disabled={busy}
                            size="sm"
                            aria-label={`Role for ${m.name || m.email || "member"}`}
                          />
                        ) : (
                          <RoleChip roleKey={m.roleKey} builtin={role?.builtin !== false} label={role?.label} />
                        )}
                      </td>
                      <td style={S.td}>
                        <StatusPill status={m.status} />
                        {m.accountActive === false && (
                          <div style={{ ...S.pill("#dc2626", "#fee2e2"), marginTop: 4 }}>Login disabled</div>
                        )}
                      </td>
                      <td style={S.td}>{m.permissionCount}</td>
                      <td style={S.td}>
                        {m.customPermissions?.length ? (
                          <span style={S.pill("#7c3aed", "#ede9fe")}>{m.customPermissions.length}</span>
                        ) : (
                          <span style={{ color: "#94a3b8" }}>None</span>
                        )}
                      </td>
                      <td style={S.td}>{fmtDate(m.lastActiveAt)}</td>
                      <td style={S.td}>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {editable && (
                            <Btn size="sm" onClick={() => { setModalError(""); setOverridesFor(m); }}>
                              Exceptions
                            </Btn>
                          )}
                          {editable && m.status === "ACTIVE" && (
                            <Btn size="sm" variant="warning" onClick={() => confirmSuspend(m)}>Suspend</Btn>
                          )}
                          {editable && m.status === "SUSPENDED" && (
                            <Btn
                              size="sm"
                              variant="success"
                              onClick={() =>
                                run(() => apiOrganizerSetMemberStatus(organizerId, m.userId, "ACTIVE"), "Member reactivated.")
                              }
                            >
                              Reactivate
                            </Btn>
                          )}
                          {editable && (
                            <Btn size="sm" variant="danger" onClick={() => confirmRemove(m)}>Remove</Btn>
                          )}
                          {m.isOwner && <span style={{ color: "#94a3b8", fontSize: 11 }}>Owner - not editable</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!members.length && (
                  <tr>
                    <td style={{ ...S.td, textAlign: "center", color: "#94a3b8" }} colSpan={7}>
                      Nobody on the team yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "roles" && (
        <div style={S.card}>
          <div style={S.sectionTitle}>Roles ({catalogue.roles.length})</div>
          <div style={S.tableWrap}>
            <table style={{ ...S.table, minWidth: 720 }}>
              <thead>
                <tr>
                  <th style={S.th}>Role</th>
                  <th style={S.th}>Description</th>
                  <th style={S.th}>Permissions</th>
                  <th style={S.th}>Members</th>
                  <th style={S.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {catalogue.roles.map((r) => (
                  <tr key={r.key}>
                    <td style={S.td}>
                      <RoleChip roleKey={r.key} builtin={r.builtin} label={r.label} />
                    </td>
                    <td style={{ ...S.td, maxWidth: 340 }}>{r.description || "-"}</td>
                    <td style={S.td}>{r.permissions.length}</td>
                    <td style={S.td}>{members.filter((m) => m.roleKey === r.key).length}</td>
                    <td style={S.td}>
                      <Btn size="sm" onClick={() => { setModalError(""); setRoleModal({ role: r }); }}>
                        {r.builtin || !canManage ? "View" : "Edit"}
                      </Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {addOpen && (
        <AddMemberModal
          roles={catalogue.roles}
          busy={busy}
          error={modalError}
          onClose={() => setAddOpen(false)}
          onSubmit={handleAdd}
        />
      )}

      {roleModal && (
        <RoleEditorDialog
          role={roleModal.role ? { ...roleModal.role, memberCount: members.filter((m) => m.roleKey === roleModal.role.key).length } : null}
          permissions={catalogue.permissions}
          grantableKeys={grantableKeys}
          canEdit={canManage}
          busy={busy}
          error={modalError}
          onClose={() => setRoleModal(null)}
          onSubmit={handleSaveRole}
          onDelete={roleModal.role && !roleModal.role.builtin ? () => confirmDeleteRole(roleModal.role) : null}
        />
      )}

      {overridesFor && (
        <OverridesModal
          member={overridesFor}
          permissions={catalogue.permissions}
          roleLabel={roleByKey.get(overridesFor.roleKey)?.label}
          rolePermissions={roleByKey.get(overridesFor.roleKey)?.permissions || []}
          grantableKeys={grantableKeys}
          busy={busy}
          error={modalError}
          onClose={() => setOverridesFor(null)}
          onSubmit={handleSaveOverrides}
        />
      )}

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          consequence={confirmState.consequence}
          confirmLabel={confirmState.confirmLabel}
          variant={confirmState.variant}
          busy={busy}
          onConfirm={runConfirm}
          onClose={() => setConfirmState(null)}
        />
      )}
    </div>
  );
};

export default OrganizerTeam;
